import React, { useState, useMemo } from "react";
import ReactSlider from "react-slider";
import { useQueries } from "@tanstack/react-query";
import { fetchOnlyData } from "../../utility/adminApi";
import SelectLabels from "../../components/SelectLabelsPayMethod";
import Date from "../../super_dashboard/components/Date";
import CancelIcon from "@mui/icons-material/Cancel";
import useDefaultCurrency from "../../utility/useDefaultCurrency";

const SalesFilter = ({
  isOpen,
  onClose,
  setCurrentPage,
  currentPage,
  setFilteredData,
  filteredData,
  setShowModal,
  fetchOrders,
}) => {
  const [formData, setFormData] = useState({
    start_amount_range: 1000,
    end_amount_range: 500000,
    cashier_name: "",
    customer_name: "",
    loyalty_plan: "",
    payment_method: "",
    start_date: "",
    end_date: "",
  });
  const { data } = useDefaultCurrency("admin");
  const currency = useMemo(() => data?.currency?.symbol, [data]);

  const [
    {
      data: cashierList = [],
      isLoading: loadingCashiers,
      isError: errorCashiers,
    },
    {
      data: customerList = [],
      isLoading: loadingCustomers,
      isError: errorCustomers,
    },
    {
      data: loyaltyPlan = [],
      isLoading: loadingLoyalty,
      isError: errorLoyalty,
    },
    {
      data: paymentList = [],
      isLoading: loadingPayments,
      isError: errorPayments,
    },
  ] = useQueries({
    queries: [
      {
        queryKey: ["cashierList"],
        queryFn: () => fetchOnlyData("dashboard/cashiers"),
      },
      {
        queryKey: ["customerList"],
        queryFn: () => fetchOnlyData("dashboard/customers"),
      },
      {
        queryKey: ["loyaltyList"],
        queryFn: () => fetchOnlyData("dashboard/loyalty-plans"),
      },
      {
        queryKey: ["paymentList"],
        queryFn: () => fetchOnlyData("dashboard/payment-methods"),
      },
    ],
  });

  if (!isOpen) return null;

  if (
    loadingCashiers ||
    loadingCustomers ||
    loadingLoyalty ||
    loadingPayments
  ) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white p-6 rounded shadow">Loading filters...</div>
      </div>
    );
  }

  if (errorCashiers || errorCustomers || errorLoyalty || errorPayments) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white p-6 rounded shadow text-red-500">
          Failed to load filter data.
        </div>
      </div>
    );
  }

  const handleCashier = (name) => {
    setFormData((prev) => ({ ...prev, cashier_name: name }));
  };

  const handleCustomer = (name) => {
    setFormData((prev) => ({ ...prev, customer_name: name }));
  };

  const handleLoyaltyPlan = (name) => {
    setFormData((prev) => ({ ...prev, loyalty_plan: name }));
  };

  const handlePayment = (method) => {
    setFormData((prev) => ({ ...prev, payment_method: method }));
  };

  const handleDateChange = (year, month, day, type) => {
    setFormData((prevData) => ({
      ...prevData,
      [type]: `${year}-${month}-${day}`,
    }));
  };

  const handleSubmit = () => {
    console.log("Filter Data:", formData);
    setCurrentPage(1);
    setFilteredData(formData);
    fetchOrders(currentPage, "", formData);
    setShowModal(false);

    // Use this data as needed (API call or prop function)
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black/50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-end pr-2  border-b-2">
          <CancelIcon
            onClick={onClose}
            sx={{
              fontSize: { xs: 20, sm: 15, md: 30 },
            }}
            className="cursor-pointer text-black mb-2"
          />
        </div>
        <h2 className="text-2xl font-semibold text-center mb-6">Filters</h2>

        <div className="space-y-4">
          {/* Amount Range */}
          <div>
            <label className="block font-medium mb-2">
              Amount Range: {currency}
              {formData.start_amount_range.toLocaleString()} - {currency}
              {formData.end_amount_range.toLocaleString()}
            </label>
            <ReactSlider
              className="w-full h-2 bg-gray-200 rounded"
              thumbClassName="h-4 w-4 bg-green-500 rounded-full cursor-pointer"
              trackClassName="bg-gray-200 h-2 rounded"
              min={1000}
              max={10000000}
              step={1000}
              value={[formData.start_amount_range, formData.end_amount_range]}
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  start_amount_range: val[0],
                  end_amount_range: val[1],
                }))
              }
              pearling
              minDistance={5000}
            />
          </div>

          {/* Cashier Name */}
          <div>
            <label className="block font-medium mb-2">Cashier name</label>
            <SelectLabels
              categoryData={cashierList}
              text="Select Cashier"
              valueKey="id"
              labelKey="firstname"
              onSelectChange={handleCashier}
            />
          </div>

          {/* Customer Name */}
          <div>
            <label className="block font-medium mb-2">Customer name</label>
            <SelectLabels
              categoryData={customerList}
              text="Select Customer"
              valueKey="id"
              labelKey="name"
              onSelectChange={handleCustomer}
            />
          </div>

          {/* Loyalty */}
          <div>
            <label className="block font-medium mb-2">Loyalty applied</label>
            <SelectLabels
              categoryData={loyaltyPlan}
              text="Select loyalty"
              valueKey="id"
              labelKey="name"
              onSelectChange={handleLoyaltyPlan}
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block font-medium mb-2">Payment method</label>
            <SelectLabels
              categoryData={paymentList}
              text="Select Payment method"
              valueKey="id"
              labelKey="method_name"
              onSelectChange={handlePayment}
            />
          </div>

          {/* Two-column grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Start Date */}
            <div>
              <label className="block font-medium mb-2">Start date</label>
              <Date
                onDateChange={handleDateChange}
                type={"start_date"}
                className={"lg:w-full"}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block font-medium mb-2">End date</label>
              <Date
                onDateChange={handleDateChange}
                type={"end_date"}
                className={"lg:w-full"}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={onClose}
              className="border px-4 py-2 rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800"
            >
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFilter;
